import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import * as projectService from '../services/projectService';
import BugReportModal from './BugReportModal';

interface SidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

export function Sidebar({ isCollapsed, setIsCollapsed }: SidebarProps): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, company, token } = useAuth();
  const [showBugReportModal, setShowBugReportModal] = useState(false);

  const isAdmin = user?.role === 'company_admin' || user?.role === 'platform_admin';
  const isPlatformAdmin = user?.role === 'platform_admin';
  const isTaskBoardUser = user?.role === 'taskboard_user';
  const isContributor = user?.role === 'contributor';
  const isStakeholder = user?.role === 'stakeholder';

  // Extract project ID from URL if we're on a project page
  const projectIdMatch = location.pathname.match(/\/projects\/([a-f0-9-]+)/i);
  const currentProjectId = projectIdMatch ? projectIdMatch[1] : null;

  // State for project list
  const [projects, setProjects] = useState<any[]>([]);
  const [isProjectsExpanded, setIsProjectsExpanded] = useState(() => {
    const saved = sessionStorage.getItem('sidebar_projects_expanded');
    return saved !== null ? saved === 'true' : false; // Default collapsed
  });
  // State for current project (to get AI requirements count)
  const [currentProject, setCurrentProject] = useState<any>(null);
  const [isMainReportingExpanded, setIsMainReportingExpanded] = useState(() => {
    const saved = sessionStorage.getItem('sidebar_main_reporting_expanded');
    return saved !== null ? saved === 'true' : false; // Default collapsed
  });
  const [isToolsExpanded, setIsToolsExpanded] = useState(() => {
    const saved = sessionStorage.getItem('sidebar_tools_expanded');
    return saved !== null ? saved === 'true' : false; // Default collapsed
  });
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(() => {
    const saved = sessionStorage.getItem('sidebar_settings_expanded');
    return saved !== null ? saved === 'true' : false; // Default collapsed
  });
  const [isAgileExpanded, setIsAgileExpanded] = useState(() => {
    const saved = sessionStorage.getItem('sidebar_agile_expanded');
    return saved !== null ? saved === 'true' : false; // Default collapsed
  });
  const [isFinancesExpanded, setIsFinancesExpanded] = useState(() => {
    const saved = sessionStorage.getItem('sidebar_finances_expanded');
    return saved !== null ? saved === 'true' : false; // Default collapsed
  });

  const isProjectManager = user?.role === 'project_manager' || user?.role === 'project_manager_lite' || isAdmin;

  // Load projects on mount
  useEffect(() => {
    async function loadProjects() {
      if (!token) return;
      try {
        const response = await projectService.listProjects({}, token);
        setProjects(response.projects || []);
      } catch (error) {
        console.error('Error loading projects:', error);
      }
    }
    loadProjects();
  }, [token]);

  // Load current project details when projectId changes
  useEffect(() => {
    async function loadCurrentProject() {
      if (!token || !currentProjectId) {
        setCurrentProject(null);
        return;
      }
      try {
        const response = await projectService.getProjectById(currentProjectId, token);
        setCurrentProject(response.project);
      } catch (error) {
        console.error('Error loading current project:', error);
      }
    }
    loadCurrentProject();
  }, [token, currentProjectId]);

  const menuItems = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      label: 'Dashboard',
      path: '/dashboard',
      show: !isTaskBoardUser
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
      ),
      label: 'Projects',
      path: '/projects',
      show: !isTaskBoardUser && !isStakeholder
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      label: 'AI Configuration',
      path: '/admin/ai-config',
      show: isPlatformAdmin && !isTaskBoardUser
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
        </svg>
      ),
      label: 'Taskboard',
      path: '/taskboard',
      show: !isStakeholder
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
        </svg>
      ),
      label: 'Sprint Archive',
      path: '/taskboard/sprint-archive',
      show: !isStakeholder
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      label: 'Reporting',
      path: '/reports',
      show: ((isProjectManager || isTaskBoardUser) && !isContributor && !isStakeholder) || isAdmin || user?.role === 'project_manager',
      isExpandable: true
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
      ),
      label: 'Release Notes',
      path: '/reports/release-notes',
      show: isPlatformAdmin
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      label: 'Blog Management',
      path: '/admin/blog',
      show: isPlatformAdmin
    }
  ];

  // Settings menu items (shown in Settings section for admins)
  const settingsMenuItems = [
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      label: 'Manage Users',
      path: '/users',
      show: isAdmin && !isTaskBoardUser
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      label: 'Manage Teams',
      path: '/teams',
      show: isAdmin && !isTaskBoardUser
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      label: 'Company Settings',
      path: '/company/settings',
      show: isAdmin && !isTaskBoardUser
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      ),
      label: 'JIRA Integration',
      path: '/company/jira',
      show: isAdmin && !isTaskBoardUser
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
      label: 'JIRA Webhook Logs',
      path: '/admin/jira-webhooks',
      show: isPlatformAdmin
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      label: 'Report a Bug',
      path: '',
      action: () => setShowBugReportModal(true),
      show: isProjectManager && !isTaskBoardUser && (isPlatformAdmin || !!company?.bug_reporting_enabled)
    }
  ];

  // Main reporting menu items (shown in expandable Reporting section)
  // Hidden for contributors - they don't have access to reporting features
  const mainReportingItems = [
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      label: 'Sprint Report',
      path: '/reports/sprint',
      show: isProjectManager && !isTaskBoardUser && !isContributor
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      label: 'Velocity Report',
      path: '/reports/velocity',
      show: isProjectManager && !isTaskBoardUser && !isContributor
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      label: 'Time Tracking',
      path: '/reports/time-tracking',
      show: true
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="7" cy="17" r="2" strokeWidth={2} />
          <circle cx="12" cy="9" r="2" strokeWidth={2} />
          <circle cx="17" cy="13" r="2" strokeWidth={2} />
          <circle cx="9" cy="5" r="2" strokeWidth={2} />
        </svg>
      ),
      label: 'Requirement Aging',
      path: '/reports/requirement-aging',
      show: true
    },
  ];

  // Leadership reporting items (shown in expandable Leadership Reporting section)
  // Only visible to project_manager, company_admin, and platform_admin (not project_manager_lite)
  const leadershipReportingItems = [
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      label: 'Leadership Report',
      path: '/reports/leadership',
      show: isAdmin || user?.role === 'project_manager'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      label: 'Program Gantt',
      path: '/reports/program-gantt',
      show: isAdmin || user?.role === 'project_manager'
    },
  ];

  // Project-specific menu items (shown when viewing a project)
  // Contributors don't have access to AI features, JIRA, or Reporting
  const projectMenuItems = currentProjectId ? [
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      label: 'Project Approvals',
      path: `/projects/${currentProjectId}/approvals`,
      show: isProjectManager
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      label: 'Requirements',
      path: `/projects/${currentProjectId}/requirements`,
      show: true
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      label: 'AI Req Generator',
      path: `/projects/${currentProjectId}/ai-requirements`,
      show: !isContributor && user?.role !== 'project_manager_lite',
      group: 'tools'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      label: 'Requirement Metrics',
      path: `/projects/${currentProjectId}/requirements/metrics`,
      show: !isContributor,
      group: 'reporting'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      label: 'Metrics',
      path: `/projects/${currentProjectId}/metrics`,
      show: !isContributor,
      group: 'reporting'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 2.057V1.001A1.001 1.001 0 0 0 23.013 0z"/>
        </svg>
      ),
      label: 'JIRA Info',
      path: `/projects/${currentProjectId}/jira`,
      show: !isContributor,
      group: 'tools'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      label: 'Timeline / Gantt Chart',
      path: `/projects/${currentProjectId}/gantt`,
      show: true,
      group: 'tools'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      ),
      label: 'Whiteboard',
      path: `/projects/${currentProjectId}/whiteboard`,
      show: true,
      group: 'tools'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      label: 'AI Insights',
      path: `/projects/${currentProjectId}/ai-insights`,
      show: !isContributor,
      group: 'reporting'
    }
  ] : [];

  const visibleMenuItems = menuItems.filter(item => item.show);
  const visibleProjectMenuItems = projectMenuItems.filter(item => item.show);

  const isActive = (path: string) => location.pathname === path;

  return (
    <div
      className={`${
        isCollapsed ? 'w-20' : 'w-64'
      } bg-surface-primary border-r border-border-primary transition-all duration-300 ease-in-out flex flex-col`}
    >
      {/* Sidebar Header */}
      <div className="h-16 flex items-center justify-center px-4 border-b border-border-primary">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-2 rounded-md hover:bg-background-secondary transition-colors"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <svg className="w-5 h-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          )}
        </button>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {visibleMenuItems.map((item) => (
          <div key={item.path}>
            <button
              onClick={() => {
                if (item.label === 'Projects') {
                  const newState = !isProjectsExpanded;
                  setIsProjectsExpanded(newState);
                  sessionStorage.setItem('sidebar_projects_expanded', String(newState));
                } else if (item.label === 'Reporting') {
                  const newState = !isMainReportingExpanded;
                  setIsMainReportingExpanded(newState);
                  sessionStorage.setItem('sidebar_main_reporting_expanded', String(newState));
                } else {
                  navigate(item.path);
                }
              }}
              className={`w-full flex items-center ${
                isCollapsed ? 'justify-center px-4' : 'px-6'
              } py-3 transition-colors ${
                isActive(item.path) || (item.label === 'Reporting' && location.pathname.startsWith('/reports')) || (item.label === 'Dashboard' && (location.pathname === '/company/dashboard-v2' || location.pathname === '/pm/dashboard-v2' || location.pathname === '/admin/dashboard-v2'))
                  ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-r-4 border-indigo-600 dark:border-indigo-400'
                  : 'text-text-primary hover:bg-background-secondary'
              }`}
              title={isCollapsed ? item.label : undefined}
            >
              <span className={isActive(item.path) || (item.label === 'Reporting' && location.pathname.startsWith('/reports')) || (item.label === 'Dashboard' && (location.pathname === '/company/dashboard-v2' || location.pathname === '/pm/dashboard-v2' || location.pathname === '/admin/dashboard-v2')) ? 'text-indigo-600 dark:text-indigo-400' : 'text-text-secondary'}>
                {item.icon}
              </span>
              {!isCollapsed && (
                <>
                  <span className="ml-3 text-sm font-medium flex-1 text-left">{item.label}</span>
                  {item.label === 'Projects' && projects.length > 0 && (
                    <svg
                      className={`w-4 h-4 text-text-tertiary transition-transform duration-200 ${
                        isProjectsExpanded ? 'rotate-90' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                  {item.label === 'Reporting' && (
                    <svg
                      className={`w-4 h-4 text-text-tertiary transition-transform duration-200 ${
                        isMainReportingExpanded ? 'rotate-90' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </>
              )}
            </button>

            {/* Nested Project List */}
            {item.label === 'Projects' && isProjectsExpanded && !isCollapsed && projects.length > 0 && (
              <div className="bg-background-secondary">
                {/* My Projects */}
                {projects.some(p => p.owner_id === user?.id) && (
                  <>
                    <div className="flex items-center justify-between px-10 py-2 bg-gradient-to-r from-indigo-600 to-blue-600">
                      <div className="flex items-center gap-1.5">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                        </svg>
                        <span className="text-xs font-semibold text-white uppercase tracking-wider">My Projects</span>
                      </div>
                      <span className="w-5 h-5 rounded-full bg-white/20 text-white text-[10px] font-bold flex items-center justify-center">
                        {projects.filter(p => p.owner_id === user?.id).length}
                      </span>
                    </div>
                    {projects.filter(p => p.owner_id === user?.id).map((project) => (
                      <button
                        key={project.id}
                        onClick={() => navigate(`/projects/${project.id}`)}
                        className={`w-full flex items-center pl-14 pr-6 py-2 text-sm transition-colors ${
                          location.pathname === `/projects/${project.id}`
                            ? 'text-indigo-600 dark:text-indigo-400 font-medium bg-indigo-50 dark:bg-indigo-900/30'
                            : 'text-text-secondary hover:bg-background-tertiary hover:text-text-primary'
                        }`}
                        title={project.name}
                      >
                        <span className="truncate">{project.name}</span>
                      </button>
                    ))}
                  </>
                )}
                {/* Team Projects */}
                {projects.some(p => p.owner_id !== user?.id) && (
                  <>
                    <div className="flex items-center justify-between px-10 py-2 mt-1 border-t border-border-primary bg-gradient-to-r from-indigo-600 to-blue-600">
                      <div className="flex items-center gap-1.5">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v1h8v-1zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-1a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 17v1h-3zM4.75 14.094A5.973 5.973 0 004 17v1H1v-1a3 3 0 013.75-2.906z" />
                        </svg>
                        <span className="text-xs font-semibold text-white uppercase tracking-wider">Team Projects</span>
                      </div>
                      <span className="w-5 h-5 rounded-full bg-white/20 text-white text-[10px] font-bold flex items-center justify-center">
                        {projects.filter(p => p.owner_id !== user?.id).length}
                      </span>
                    </div>
                    {projects.filter(p => p.owner_id !== user?.id).map((project) => (
                      <button
                        key={project.id}
                        onClick={() => navigate(`/projects/${project.id}`)}
                        className={`w-full flex items-center pl-14 pr-6 py-2 text-sm transition-colors ${
                          location.pathname === `/projects/${project.id}`
                            ? 'text-indigo-600 dark:text-indigo-400 font-medium bg-indigo-50 dark:bg-indigo-900/30'
                            : 'text-text-secondary hover:bg-background-tertiary hover:text-text-primary'
                        }`}
                        title={project.name}
                      >
                        <span className="truncate">{project.name}</span>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}

            {/* Nested Reporting Menu with sub-headers */}
            {item.label === 'Reporting' && isMainReportingExpanded && !isCollapsed && (
              <div className="bg-background-secondary">
                {/* Taskboard Reporting sub-header */}
                {mainReportingItems.some(r => r.show) && (
                  <>
                    <div className="flex items-center gap-1.5 px-10 pt-3 pb-1 bg-gradient-to-r from-indigo-600 to-blue-600">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                      </svg>
                      <span className="text-xs font-semibold text-white uppercase tracking-wider">Taskboard Reporting</span>
                    </div>
                    {mainReportingItems.filter(r => r.show).map((reportItem) => (
                      <button
                        key={reportItem.path}
                        onClick={() => navigate(reportItem.path)}
                        className={`w-full flex items-center pl-14 pr-6 py-2 text-sm transition-colors ${
                          location.pathname === reportItem.path
                            ? 'text-indigo-600 dark:text-indigo-400 font-medium bg-indigo-50 dark:bg-indigo-900/30'
                            : 'text-text-secondary hover:bg-background-tertiary hover:text-text-primary'
                        }`}
                        title={reportItem.label}
                      >
                        <span className="truncate">{reportItem.label}</span>
                      </button>
                    ))}
                  </>
                )}
                {/* Leadership Reporting sub-header */}
                {leadershipReportingItems.some(r => r.show) && (
                  <>
                    <div className="flex items-center gap-1.5 px-10 pt-3 pb-1 mt-1 border-t border-border-primary bg-gradient-to-r from-indigo-600 to-blue-600">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span className="text-xs font-semibold text-white uppercase tracking-wider">Leadership Reporting</span>
                    </div>
                    {leadershipReportingItems.filter(r => r.show).map((reportItem) => (
                      <button
                        key={reportItem.path}
                        onClick={() => navigate(reportItem.path)}
                        className={`w-full flex items-center pl-14 pr-6 py-2 text-sm transition-colors ${
                          location.pathname === reportItem.path
                            ? 'text-indigo-600 dark:text-indigo-400 font-medium bg-indigo-50 dark:bg-indigo-900/30'
                            : 'text-text-secondary hover:bg-background-tertiary hover:text-text-primary'
                        }`}
                        title={reportItem.label}
                      >
                        <span className="truncate">{reportItem.label}</span>
                      </button>
                    ))}
                  </>
                )}
                {/* Project Reporting sub-header (only when viewing a project) */}
                {currentProjectId && visibleProjectMenuItems.filter(item => item.group === 'reporting').length > 0 && (
                  <>
                    <div className="flex items-center gap-1.5 px-10 pt-3 pb-1 mt-1 border-t border-border-primary bg-gradient-to-r from-indigo-600 to-blue-600">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                      <span className="text-xs font-semibold text-white uppercase tracking-wider">Project Reporting</span>
                    </div>
                    {visibleProjectMenuItems.filter(item => item.group === 'reporting').map((item) => (
                      <button
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        className={`w-full flex items-center pl-14 pr-6 py-2 text-sm transition-colors ${
                          location.pathname === item.path
                            ? 'text-indigo-600 dark:text-indigo-400 font-medium bg-indigo-50 dark:bg-indigo-900/30'
                            : 'text-text-secondary hover:bg-background-tertiary hover:text-text-primary'
                        }`}
                        title={item.label}
                      >
                        <span className="truncate">{item.label}</span>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        ))}

        {/* My Projects section for stakeholders */}
        {isStakeholder && projects.length > 0 && (
          <>
            <div className="my-3 px-6">
              <div className="border-t border-border-primary"></div>
            </div>
            {!isCollapsed && (
              <div className="px-6 py-2">
                <span className="text-xs font-semibold text-text-tertiary uppercase tracking-wider">
                  My Projects
                </span>
              </div>
            )}
            {projects.map((project) => (
              <button
                key={project.id}
                onClick={() => navigate(`/projects/${project.id}`)}
                className={`w-full flex items-center ${
                  isCollapsed ? 'justify-center px-4' : 'px-6'
                } py-2.5 transition-colors ${
                  location.pathname === `/projects/${project.id}`
                    ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-r-4 border-indigo-600 dark:border-indigo-400'
                    : 'text-text-primary hover:bg-background-secondary'
                }`}
                title={isCollapsed ? project.name : undefined}
              >
                <span className={location.pathname === `/projects/${project.id}` ? 'text-indigo-600 dark:text-indigo-400' : 'text-text-secondary'}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                </span>
                {!isCollapsed && (
                  <span className="ml-3 text-sm font-medium truncate">{project.name}</span>
                )}
              </button>
            ))}
          </>
        )}

        {/* Project-specific navigation (shown when viewing a project, hidden for stakeholders) */}
        {!isStakeholder && visibleProjectMenuItems.length > 0 && (
          <>
            <div className="my-3 px-6">
              <div className="border-t border-border-primary"></div>
            </div>
            {!isCollapsed && (
              <div className="px-6 py-2">
                <span className="text-xs font-semibold text-text-tertiary uppercase tracking-wider">
                  Project
                </span>
              </div>
            )}
            {visibleProjectMenuItems.filter(item => !item.group).map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center ${
                  isCollapsed ? 'justify-center px-4' : 'px-6'
                } py-2.5 transition-colors ${
                  isActive(item.path)
                    ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-r-4 border-indigo-600 dark:border-indigo-400'
                    : 'text-text-primary hover:bg-background-secondary'
                }`}
                title={isCollapsed ? item.label : undefined}
              >
                <span className={isActive(item.path) ? 'text-indigo-600 dark:text-indigo-400' : 'text-text-secondary'}>
                  {item.icon}
                </span>
                {!isCollapsed && (
                  <span className="ml-3 text-sm font-medium">{item.label}</span>
                )}
              </button>
            ))}

            {/* Tools group */}
            {visibleProjectMenuItems.some(item => item.group === 'tools') && (
              <>
                <button
                  onClick={() => {
                    const next = !isToolsExpanded;
                    setIsToolsExpanded(next);
                    sessionStorage.setItem('sidebar_tools_expanded', String(next));
                  }}
                  className={`w-full flex items-center ${
                    isCollapsed ? 'justify-center px-4' : 'px-6'
                  } py-2.5 transition-colors text-text-primary hover:bg-background-secondary`}
                  title={isCollapsed ? 'Tools' : undefined}
                >
                  <span className="text-text-secondary">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243 4.243 3 3 0 004.243-4.243zm0-5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243z" />
                    </svg>
                  </span>
                  {!isCollapsed && (
                    <>
                      <span className="ml-3 text-sm font-medium">Tools</span>
                      <svg
                        className={`ml-auto w-4 h-4 text-text-tertiary transition-transform ${isToolsExpanded ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>
                {(isToolsExpanded || isCollapsed) && visibleProjectMenuItems.filter(item => item.group === 'tools').map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`w-full flex items-center ${
                      isCollapsed ? 'justify-center px-4' : 'pl-10 pr-6'
                    } py-2 transition-colors ${
                      isActive(item.path)
                        ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-r-4 border-indigo-600 dark:border-indigo-400'
                        : 'text-text-primary hover:bg-background-secondary'
                    }`}
                    title={isCollapsed ? item.label : undefined}
                  >
                    <span className={isActive(item.path) ? 'text-indigo-600 dark:text-indigo-400' : 'text-text-secondary'}>
                      {item.icon}
                    </span>
                    {!isCollapsed && (
                      <span className="ml-3 text-sm font-medium">{item.label}</span>
                    )}
                  </button>
                ))}
              </>
            )}
          </>
        )}

        {/* Agile section (shown for PM+ roles) */}
        {isProjectManager && !isTaskBoardUser && !isStakeholder && (
          <>
            <div className="my-3 px-6">
              <div className="border-t border-border-primary"></div>
            </div>
            {!isCollapsed && (
              <div className="px-6 py-2">
                <span className="text-xs font-semibold text-text-tertiary uppercase tracking-wider">
                  Agile
                </span>
              </div>
            )}

            <button
              onClick={() => {
                const next = !isAgileExpanded;
                setIsAgileExpanded(next);
                sessionStorage.setItem('sidebar_agile_expanded', String(next));
              }}
              className={`w-full flex items-center ${
                isCollapsed ? 'justify-center px-4' : 'px-6'
              } py-2.5 transition-colors text-text-primary hover:bg-background-secondary`}
              title={isCollapsed ? 'Agile' : undefined}
            >
              <span className="text-text-secondary">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </span>
              {!isCollapsed && (
                <>
                  <span className="ml-3 text-sm font-medium">Agile</span>
                  <svg
                    className={`ml-auto w-4 h-4 text-text-tertiary transition-transform ${isAgileExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
            {(isAgileExpanded || isCollapsed) && (
              <button
                onClick={() => navigate('/retrospectives')}
                className={`w-full flex items-center ${
                  isCollapsed ? 'justify-center px-4' : 'pl-10 pr-6'
                } py-2 transition-colors ${
                  isActive('/retrospectives')
                    ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-r-4 border-indigo-600 dark:border-indigo-400'
                    : 'text-text-primary hover:bg-background-secondary'
                }`}
                title={isCollapsed ? 'Retrospectives' : undefined}
              >
                <span className={isActive('/retrospectives') ? 'text-indigo-600 dark:text-indigo-400' : 'text-text-secondary'}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </span>
                {!isCollapsed && (
                  <span className="ml-3 text-sm font-medium">Retrospectives</span>
                )}
              </button>
            )}
          </>
        )}

        {/* Nivoon Finances section (platform_admin only) */}
        {isPlatformAdmin && (
          <>
            <div className="my-3 px-6">
              <div className="border-t border-border-primary"></div>
            </div>
            {!isCollapsed && (
              <div className="px-6 py-2">
                <span className="text-xs font-semibold text-text-tertiary uppercase tracking-wider">
                  Nivoon Finances
                </span>
              </div>
            )}

            <button
              onClick={() => {
                const next = !isFinancesExpanded;
                setIsFinancesExpanded(next);
                sessionStorage.setItem('sidebar_finances_expanded', String(next));
              }}
              className={`w-full flex items-center ${
                isCollapsed ? 'justify-center px-4' : 'px-6'
              } py-2.5 transition-colors text-text-primary hover:bg-background-secondary`}
              title={isCollapsed ? 'Finances' : undefined}
            >
              <span className="text-text-secondary">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              {!isCollapsed && (
                <>
                  <span className="ml-3 text-sm font-medium">Finances</span>
                  <svg
                    className={`ml-auto w-4 h-4 text-text-tertiary transition-transform ${isFinancesExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
            {(isFinancesExpanded || isCollapsed) && (
              <>
                <button
                  onClick={() => navigate('/admin/finances')}
                  className={`w-full flex items-center ${
                    isCollapsed ? 'justify-center px-4' : 'pl-10 pr-6'
                  } py-2 transition-colors ${
                    isActive('/admin/finances')
                      ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-r-4 border-indigo-600 dark:border-indigo-400'
                      : 'text-text-primary hover:bg-background-secondary'
                  }`}
                  title={isCollapsed ? 'Finances Dashboard' : undefined}
                >
                  <span className={isActive('/admin/finances') ? 'text-indigo-600 dark:text-indigo-400' : 'text-text-secondary'}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </span>
                  {!isCollapsed && (
                    <span className="ml-3 text-sm font-medium">Dashboard</span>
                  )}
                </button>
                <button
                  onClick={() => navigate('/admin/pricing-plans')}
                  className={`w-full flex items-center ${
                    isCollapsed ? 'justify-center px-4' : 'pl-10 pr-6'
                  } py-2 transition-colors ${
                    isActive('/admin/pricing-plans')
                      ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-r-4 border-indigo-600 dark:border-indigo-400'
                      : 'text-text-primary hover:bg-background-secondary'
                  }`}
                  title={isCollapsed ? 'Pricing Plans' : undefined}
                >
                  <span className={isActive('/admin/pricing-plans') ? 'text-indigo-600 dark:text-indigo-400' : 'text-text-secondary'}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </span>
                  {!isCollapsed && (
                    <span className="ml-3 text-sm font-medium">Pricing Plans</span>
                  )}
                </button>
                <button
                  onClick={() => navigate('/admin/company-subscriptions')}
                  className={`w-full flex items-center ${
                    isCollapsed ? 'justify-center px-4' : 'pl-10 pr-6'
                  } py-2 transition-colors ${
                    isActive('/admin/company-subscriptions')
                      ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-r-4 border-indigo-600 dark:border-indigo-400'
                      : 'text-text-primary hover:bg-background-secondary'
                  }`}
                  title={isCollapsed ? 'Subscriptions' : undefined}
                >
                  <span className={isActive('/admin/company-subscriptions') ? 'text-indigo-600 dark:text-indigo-400' : 'text-text-secondary'}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </span>
                  {!isCollapsed && (
                    <span className="ml-3 text-sm font-medium">Subscriptions</span>
                  )}
                </button>
                <button
                  onClick={() => navigate('/admin/stripe-payments')}
                  className={`w-full flex items-center ${
                    isCollapsed ? 'justify-center px-4' : 'pl-10 pr-6'
                  } py-2 transition-colors ${
                    isActive('/admin/stripe-payments')
                      ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-r-4 border-indigo-600 dark:border-indigo-400'
                      : 'text-text-primary hover:bg-background-secondary'
                  }`}
                  title={isCollapsed ? 'Stripe Payments' : undefined}
                >
                  <span className={isActive('/admin/stripe-payments') ? 'text-indigo-600 dark:text-indigo-400' : 'text-text-secondary'}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </span>
                  {!isCollapsed && (
                    <span className="ml-3 text-sm font-medium">Stripe Payments</span>
                  )}
                </button>
              </>
            )}
          </>
        )}

        {/* Settings section (shown for admins) */}
        {settingsMenuItems.some(item => item.show) && (
          <>
            <div className="my-3 px-6">
              <div className="border-t border-border-primary"></div>
            </div>
            {!isCollapsed && (
              <div className="px-6 py-2">
                <span className="text-xs font-semibold text-text-tertiary uppercase tracking-wider">
                  Settings
                </span>
              </div>
            )}

            {/* Settings collapsible bucket */}
            <button
              onClick={() => {
                const next = !isSettingsExpanded;
                setIsSettingsExpanded(next);
                sessionStorage.setItem('sidebar_settings_expanded', String(next));
              }}
              className={`w-full flex items-center ${
                isCollapsed ? 'justify-center px-4' : 'px-6'
              } py-2.5 transition-colors text-text-primary hover:bg-background-secondary`}
              title={isCollapsed ? 'Settings' : undefined}
            >
              <span className="text-text-secondary">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </span>
              {!isCollapsed && (
                <>
                  <span className="ml-3 text-sm font-medium">Settings</span>
                  <svg
                    className={`ml-auto w-4 h-4 text-text-tertiary transition-transform ${isSettingsExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
            {(isSettingsExpanded || isCollapsed) && settingsMenuItems.filter(item => item.show).map((item) => (
              <button
                key={item.label}
                onClick={() => item.action ? item.action() : navigate(item.path)}
                className={`w-full flex items-center ${
                  isCollapsed ? 'justify-center px-4' : 'pl-10 pr-6'
                } py-2 transition-colors ${
                  item.path && isActive(item.path)
                    ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-r-4 border-indigo-600 dark:border-indigo-400'
                    : 'text-text-primary hover:bg-background-secondary'
                }`}
                title={isCollapsed ? item.label : undefined}
              >
                <span className={item.path && isActive(item.path) ? 'text-indigo-600 dark:text-indigo-400' : 'text-text-secondary'}>
                  {item.icon}
                </span>
                {!isCollapsed && (
                  <span className="ml-3 text-sm font-medium">{item.label}</span>
                )}
              </button>
            ))}
          </>
        )}
      </nav>

      {/* Bug Report Modal */}
      <BugReportModal
        isOpen={showBugReportModal}
        onClose={() => setShowBugReportModal(false)}
      />
    </div>
  );
}

export default Sidebar;
